import { type useFinchIntegration } from '../hooks/useFinchIntegration'
import { type useKomboIntegration } from '../hooks/useKomboIntegration'
import { type useMergeIntegration } from '../hooks/useMergeIntegration'

// Reuse the same constants from provider strategies
export const API_CONNECTION_METHOD = {
  FINCH: 'finch',
  MERGE_DEV: 'merge_dev',
  KOMBO: 'kombo',
  ADP: 'adp',
} as const

type ApiConnectionMethod = typeof API_CONNECTION_METHOD[keyof typeof API_CONNECTION_METHOD]

interface ReauthenticationStrategy {
  startReauthentication: () => Promise<void>
  openModal: () => void
  isModalReady: () => boolean
  cleanup: () => void
}

interface StrategyOptions {
  onSuccess?: () => void
  payrollProviderConfigId?: string
}

export function createPayrollReauthenticationStrategies (
  finchIntegration: ReturnType<typeof useFinchIntegration>,
  mergeIntegration: ReturnType<typeof useMergeIntegration>,
  komboIntegration: ReturnType<typeof useKomboIntegration>,
  { onSuccess: _onSuccess, payrollProviderConfigId }: StrategyOptions
): Record<ApiConnectionMethod, ReauthenticationStrategy> {
  return {
    [API_CONNECTION_METHOD.FINCH]: {
      startReauthentication: () => finchIntegration.startReauthentication(),
      openModal: finchIntegration.openModal,
      isModalReady: () => !!finchIntegration.sessionId,
      cleanup: () => finchIntegration.setProviderIdToOpen(undefined),
    },
    [API_CONNECTION_METHOD.MERGE_DEV]: {
      startReauthentication: async () => {
        if (!payrollProviderConfigId) {
          throw new Error('Provider configuration ID is required for Merge reauthentication')
        }
        // For Merge, we reuse the connection flow since there's no separate reauthentication API
        // For reauthentication, we don't need shared connection support, just the basic provider info
        await mergeIntegration.startConnection({
          id: payrollProviderConfigId,
          name: 'Merge Provider',
          icon_image_url: '',
          logo_image_url: '',
          api_connection_remote_id: '',
          api_connection_method: 'merge_dev',
          supports_automatic_deductions: false
        })
      },
      openModal: mergeIntegration.openModal,
      isModalReady: () => !!mergeIntegration.linkToken && mergeIntegration.isReady,
      cleanup: () => mergeIntegration.setProviderIdToOpen(undefined),
    },
    [API_CONNECTION_METHOD.KOMBO]: {
      startReauthentication: () => komboIntegration.startReauthentication(),
      openModal: komboIntegration.openModal,
      isModalReady: () => !!komboIntegration.reconnectLink,
      cleanup: () => komboIntegration.setProviderIdToOpen(undefined),
    },
    [API_CONNECTION_METHOD.ADP]: {
      startReauthentication: () => {
        throw new Error('ADP reauthentication is not supported')
      },
      openModal: () => {
        throw new Error('ADP reauthentication is not supported')
      },
      isModalReady: () => false,
      cleanup: () => {},
    },
  }
}
